import { brand } from "../../../config/theme";
import type {
  AppSidebarMenuProps,
  SidebarMenuItem,
  SidebarMenuChild,
  CompanyIndicatorProps,
  FooterStatsProps,
  SidebarThemeClasses,
} from "./types";

/**
 * Internal sidebar theme classes matching the admin layout's exact design.
 * This ensures every app sidebar looks identical regardless of the layout's own themeClasses.
 */
function getSidebarTheme(): SidebarThemeClasses {
  return {
    sidebarBg: "bg-surface",
    border: "border-line",
    text: "text-content",
    textSecondary: "text-content-secondary",
    cardBg: "bg-card",
    cardBorder: "border-line",
    buttonHover: "hover:bg-subtle",
    buttonText: "text-content-secondary",
    buttonTextHover: "hover:text-content",
  };
}

function CompanyIndicator({
  companyId,
  companyName,
  isReseller,
  themeClasses,
}: CompanyIndicatorProps & { themeClasses: SidebarThemeClasses }) {
  return (
    <div
      className={`mb-4 p-3 rounded-xl border ${
        companyId ? "" : "bg-subtle border-line"
      }`}
      style={
        companyId
          ? {
              backgroundColor: `${brand.primary}1A`,
              borderColor: `${brand.primary}4D`,
            }
          : undefined
      }
    >
      <div className="flex items-center space-x-2">
        <i
          className={`ri-building-2-line text-lg ${
            companyId ? "" : themeClasses.textSecondary
          }`}
          style={companyId ? { color: brand.primary } : undefined}
        ></i>
        <div className="flex-1">
          <p
            className={`text-xs font-medium ${
              companyId ? "" : themeClasses.textSecondary
            }`}
            style={companyId ? { color: brand.primary } : undefined}
          >
            {companyId ? "Compañía seleccionada" : "Sin compañía seleccionada"}
          </p>
          {companyId && companyName ? (
            <p className={`text-sm font-semibold ${themeClasses.text}`}>
              {companyName}
            </p>
          ) : (
            <p className={`text-xs ${themeClasses.textSecondary} mt-0.5`}>
              {isReseller
                ? "Selecciona una compañía en el header"
                : "Contacta a tu administrador"}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function MenuItemButton({
  item,
  isActive,
  themeClasses,
  onNavigate,
  hasChildren,
  expandedMenus,
  onToggleSubmenu,
  isChildActive,
  onCloseMobileMenu,
}: {
  item: SidebarMenuItem;
  isActive: boolean;
  themeClasses: SidebarThemeClasses;
  onNavigate: (path: string) => void;
  hasChildren: boolean;
  expandedMenus?: string[];
  onToggleSubmenu?: (menuId: string) => void;
  isChildActive: boolean;
  onCloseMobileMenu: () => void;
}) {
  const isExpanded = expandedMenus?.includes(item.id) ?? false;
  const path = item.path || item.route || "";

  return (
    <div>
      <button
          disabled={item.disabled}
          title={item.disabled ? item.tooltip || "" : ""}
          onClick={() => {
            if (item.disabled) return;
            if (hasChildren && onToggleSubmenu) {
              onToggleSubmenu(item.id);
              return;
            }
            onNavigate(path);
            onCloseMobileMenu();
          }}
          className={`w-full flex items-center justify-between p-2.5 sm:p-3 rounded-xl transition-all
            ${
              item.disabled
                ? "opacity-50 cursor-not-allowed"
                : isActive
                  ? "shadow-lg"
                  : `${themeClasses.buttonText} ${themeClasses.buttonTextHover} ${themeClasses.buttonHover}`
            }
          `}
          style={
            isActive && !item.disabled
              ? { backgroundColor: brand.primary }
              : undefined
          }
        >
          {/* LEFT */}
          <div className="flex items-center space-x-3">
            <i className={`${item.icon} text-lg flex-shrink-0`}></i>
            <span className="font-medium text-sm xs:text-base truncate">
              {item.label}
            </span>
          </div>

          {/* RIGHT SIDE */}
          <div className="flex items-center space-x-2">
            {/* Count */}
            {item.loading ? null : item.count !== undefined ? (
              <span
                className="text-xs px-2 py-1 rounded-full bg-subtle text-content-secondary"
              >
                {item.count}
              </span>
            ) : null}

            {/* Expand/collapse arrow for children */}
            {hasChildren && (
              <i
                className={`ri-arrow-${isExpanded ? "up" : "down"}-s-line text-lg ${
                  isExpanded || isChildActive
                    ? "text-primary-500"
                    : themeClasses.buttonText
                }`}
              ></i>
            )}
          </div>
        </button>

      {/* Sub-menu items */}
      {hasChildren && isExpanded && item.children && (
        <div className="ml-4 mt-1 space-y-1">
          {item.children?.map((child: SidebarMenuChild) => {
            const isChildItemActive =
              typeof window !== "undefined" &&
              child.path &&
              (window.location.pathname === child.path ||
                window.location.pathname.startsWith(child.path + "/"));
            const childHasChildren =
              !!child.children && child.children.length > 0;
            const isChildExpanded =
              expandedMenus?.includes(child.id) ?? false;
            return (
              <div key={child.id}>
                <button
                  onClick={() => {
                    if (child.action) {
                      child.action();
                    } else if (child.path) {
                      onNavigate(child.path);
                      // Reveal nested sub-options (e.g. "Modelos") on navigate.
                      if (childHasChildren && !isChildExpanded) {
                        onToggleSubmenu?.(child.id);
                      }
                    }
                    onCloseMobileMenu();
                  }}
                  className={`w-full flex items-center justify-between p-2 pl-4 rounded-lg transition-all text-left ${
                    isChildItemActive
                      ? "shadow-lg"
                      : `${themeClasses.buttonText} ${themeClasses.buttonTextHover} ${themeClasses.buttonHover}`
                  }`}
                  style={
                    isChildItemActive
                      ? { backgroundColor: brand.primary, color: "white" }
                      : undefined
                  }
                >
                  <div className="flex items-center min-w-0">
                    <i
                      className={`${child.icon} text-sm mr-2 flex-shrink-0`}
                    ></i>
                    <span className="text-sm truncate">{child.label}</span>
                  </div>
                  <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
                    {child.loading ? null : child.count !== undefined ? (
                      <span
                        className="text-xs px-2 py-0.5 rounded-full bg-subtle text-content-secondary"
                      >
                        {child.count}
                      </span>
                    ) : null}
                    {/* Expand/collapse arrow for nested sub-options */}
                    {childHasChildren && (
                      <i
                        role="button"
                        aria-label="toggle submenu"
                        onClick={(e) => {
                          e.stopPropagation();
                          onToggleSubmenu?.(child.id);
                        }}
                        className={`ri-arrow-${isChildExpanded ? "up" : "down"}-s-line text-base cursor-pointer ${
                          isChildItemActive
                            ? ""
                            : isChildExpanded
                              ? "text-primary-500"
                              : themeClasses.buttonText
                        }`}
                      ></i>
                    )}
                  </div>
                </button>

                {/* Nested sub-options (third level, e.g. Dispositivos -> Modelos) */}
                {childHasChildren && isChildExpanded && child.children && (
                  <div className="ml-4 mt-1 space-y-1">
                    {child.children.map((grandChild: SidebarMenuChild) => {
                      const isGrandActive =
                        typeof window !== "undefined" &&
                        grandChild.path &&
                        (window.location.pathname === grandChild.path ||
                          window.location.pathname.startsWith(
                            grandChild.path + "/",
                          ));
                      return (
                        <button
                          key={grandChild.id}
                          onClick={() => {
                            if (grandChild.action) {
                              grandChild.action();
                            } else if (grandChild.path) {
                              onNavigate(grandChild.path);
                            }
                            onCloseMobileMenu();
                          }}
                          className={`w-full flex items-center justify-between p-2 pl-4 rounded-lg transition-all text-left ${
                            isGrandActive
                              ? "shadow-lg"
                              : `${themeClasses.buttonText} ${themeClasses.buttonTextHover} ${themeClasses.buttonHover}`
                          }`}
                          style={
                            isGrandActive
                              ? { backgroundColor: brand.primary, color: "white" }
                              : undefined
                          }
                        >
                          <div className="flex items-center min-w-0 flex-1">
                            <i
                              className={`${grandChild.icon} text-sm mr-2 flex-shrink-0`}
                            ></i>
                            <span className="text-sm leading-tight whitespace-normal break-words text-left">
                              {grandChild.label}
                            </span>
                          </div>
                          {grandChild.loading ? null : grandChild.count !==
                            undefined ? (
                            <span
                              className="text-xs px-2 py-0.5 rounded-full flex-shrink-0 ml-2 bg-subtle text-content-secondary"
                            >
                              {grandChild.count}
                            </span>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Divider */}
      {item.dividerAfter && (
        <div className={`my-2 border-t ${themeClasses.border}`} />
      )}

      {/* Tooltip */}
      {item.disabled && item.tooltip && (
        <div
          className="
            absolute bottom-full left-1/2 -translate-x-1/2 mb-2
            bg-black text-white text-xs px-2 py-1 rounded
            opacity-0 group-hover:opacity-100
            pointer-events-none transition-all duration-200
            whitespace-nowrap z-50
          "
        >
          {item.tooltip}
        </div>
      )}
    </div>
  );
}

function FooterStats({
  footerStats,
  themeClasses,
}: {
  footerStats: FooterStatsProps;
  themeClasses: SidebarThemeClasses;
}) {
  return (
    <div
      className={`mt-8 p-4 ${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl`}
    >
      <h3 className={`text-sm font-semibold ${themeClasses.text} mb-3`}>
        {footerStats.label}
      </h3>
      <div className="space-y-2">
        {footerStats.items.map((stat, index) => (
          <div key={index} className="flex justify-between">
            <span className={`text-xs ${themeClasses.textSecondary}`}>
              {stat.label}
            </span>
            <span
              className={`text-xs font-semibold ${stat.className || themeClasses.text}`}
            >
              {stat.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AppSidebarMenu({
  menuItems,
  isMobileMenuOpen,
  onCloseMobileMenu,
  onNavigate,
  isActiveRoute,
  companyIndicator,
  companySelector,
  footerStats,
  expandedMenus,
  onToggleSubmenu,
}: AppSidebarMenuProps) {
  const themeClasses = getSidebarTheme();
  return (
    <>
      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onCloseMobileMenu}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
          ${themeClasses.sidebarBg} border-r ${themeClasses.border} shadow-sm
          fixed lg:relative inset-y-0 left-0 z-50
          w-72 h-full min-h-0 transform transition-transform duration-300 ease-in-out
          ${isMobileMenuOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
          lg:block overflow-y-auto
        `}
      >
        <div className="p-4 sm:p-6">
          {/* Company Selector — solo en móvil; en desktop vive en el Header */}
          {companySelector && (
            <div className="lg:hidden mb-4">{companySelector}</div>
          )}

          {/* Company Indicator */}
          {companyIndicator && (
            <CompanyIndicator
              {...companyIndicator}
              themeClasses={themeClasses}
            />
          )}

          {/* Menu Items */}
          <nav className="space-y-1">
            {menuItems.map((item) => {
              const path = item.path || item.route || item.id;
              const isActive = isActiveRoute(item.id, path);
              const hasChildren =
                !!item.children && item.children.length > 0;
              const isChildActive =
                hasChildren &&
                !!item.children?.some((child) =>
                  isActiveRoute(child.id, child.path),
                );

              return (
                <div key={item.id} className="relative group">
                  <MenuItemButton
                    item={item}
                    isActive={isActive}
                    themeClasses={themeClasses}
                    onNavigate={onNavigate}
                    hasChildren={hasChildren}
                    expandedMenus={expandedMenus}
                    onToggleSubmenu={onToggleSubmenu}
                    isChildActive={isChildActive}
                    onCloseMobileMenu={onCloseMobileMenu}
                  />
                </div>
              );
            })}
          </nav>

          {/* Footer Stats */}
          {footerStats && (
            <FooterStats
              footerStats={footerStats}
              themeClasses={themeClasses}
            />
          )}
        </div>
      </div>
    </>
  );
}
